import { ApplyFieldDrift, DriftFieldResolution, DriftResolutionChoice } from 'src/services';

export const humanizeSegment = (segment: string): string => {
  const withSpaces = segment.replace(/[_-]/g, ' ');
  return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
};

export const formatDomainLabel = (domain: string): string =>
  domain.split('.').reverse().map(humanizeSegment).join(' · ');

export const formatFieldValue = (value: unknown): string => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

export const driftKey = (
  drift: Pick<ApplyFieldDrift, 'domain' | 'entityId' | 'entityPath' | 'valuePath'>
): string => `${drift.domain}|${drift.entityId ?? ''}|${drift.entityPath ?? ''}|${drift.valuePath}`;

export interface DriftDomainSummary {
  domain: string;
  label: string;
  /** Distinct entities (e.g. rules) when the domain is entity based, otherwise distinct settings. */
  count: number;
  unit: 'item' | 'setting';
}

/**
 * Groups drifted fields by domain with human-readable counts. Never exposes raw values or ids:
 * entities are only counted, and repeated fields (same drift key) are deduped.
 */
export const summarizeDriftByDomain = (drifted: ApplyFieldDrift[]): DriftDomainSummary[] => {
  const byDomain = new Map<string, { entities: Set<string>; settings: Set<string> }>();
  drifted.forEach(drift => {
    const group = byDomain.get(drift.domain) ?? { entities: new Set(), settings: new Set() };
    if (drift.entityId) group.entities.add(drift.entityId);
    group.settings.add(`${drift.entityPath ?? ''}|${drift.valuePath}`);
    byDomain.set(drift.domain, group);
  });

  return Array.from(byDomain.entries()).map(([domain, group]) => {
    const hasEntities = group.entities.size > 0;
    return {
      domain,
      label: formatDomainLabel(domain),
      count: hasEntities ? group.entities.size : group.settings.size,
      unit: hasEntities ? 'item' : 'setting',
    };
  });
};

export const formatDriftSummaryLine = ({ label, count, unit }: DriftDomainSummary): string =>
  `${label} — ${count} ${unit}${count === 1 ? '' : 's'} changed`;

/** One global choice -> a resolution for EVERY drifted field (the API still resolves per field). */
export const buildDriftResolutions = (
  drifted: ApplyFieldDrift[],
  choice: DriftResolutionChoice
): DriftFieldResolution[] =>
  drifted.map(drift => ({
    domain: drift.domain,
    entityId: drift.entityId,
    entityPath: drift.entityPath,
    valuePath: drift.valuePath,
    choice,
  }));

const REPLACED_WITHOUT_DRIFT_CHECK_LABELS: Record<string, string> = {
  'merchandising-rules': 'Merchandising rules',
  'ranking-strategy': 'Ranking strategy',
};

export const formatReplacesWithoutDriftCheck = (
  replaces: string[] | undefined,
  winningVariantName: string
): string | null => {
  if (!replaces?.length) return null;
  const labels = replaces.map((key, index) => {
    const label = REPLACED_WITHOUT_DRIFT_CHECK_LABELS[key] ?? key;
    return index === 0 ? label : label.toLowerCase();
  });
  const isPlural = labels.length > 1;
  return `${labels.join(' and ')} from ${winningVariantName} ${
    isPlural ? 'replace the current ones' : 'replaces the current one'
  }. Edits made since the test started will be overwritten.`;
};

export const formatApplySummary = ({
  writeCount,
  driftCount,
  choice,
}: {
  writeCount: number;
  driftCount: number;
  choice: DriftResolutionChoice;
}): string =>
  choice === 'restore-control'
    ? `${writeCount + driftCount} change(s) saved as pending, ${driftCount} drifted setting(s) restored to control`
    : `${writeCount} change(s) saved as pending, ${driftCount} drifted setting(s) left as is`;
